/**
 * JobTriageSheet — read-only PM triage popup for Execute → Active rows.
 *
 * Shows phase progress, crew on site, homeowner contact, blockers,
 * per-phase photos (as thumbnails), and recent activity. A single "Open full job →"
 * link preserves the escape hatch.
 *
 * Rules:
 *   • Read-only. No writers, no schema, no new contracts.
 *   • Reuses canonical resolvers (InstallsLiveRow + AppDataContext).
 *   • Honors Financial Visibility Policy — never renders contractValue/cost.
 */
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  ArrowUpRight,
  Calendar,
  CheckCircle2,
  CircleDot,
  Hammer,
  Home,
  Mail,
  MapPin,
  Phone,
  ShieldAlert,
  Users,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ContactBlock } from "@/components/quick-actions/ContactBlock";
import { useAppData } from "@/data/AppDataContext";
import { formatAddress } from "@/lib/addressUtils";
import { supabase } from "@/lib/supabase";
import { createSignedStorageThumbnailUrl } from "@/lib/storageImageUrls";
import type { InstallsLiveRow } from "@/data/observability/installsLiveSelector";
import type { Job } from "@/data/types";
import type { PhaseExecution } from "@/data/jobSpecExecution";
import { humanizeStatus } from "@/lib/humanize";
import { useEffect, useMemo, useState } from "react";

type Lifecycle = "pre_install" | "in_progress" | "blocked" | "complete" | string;

interface JobTriageSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: InstallsLiveRow | null;
  job?: Job;
  lifecycle?: Lifecycle;
  blockerReasons?: string[];
  blockerLabel?: string;
  blockers?: number;
}

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function phaseStatusTone(status: string | undefined): string {
  switch (status) {
    case "qa_passed":
      return "text-success border-success/40 bg-success/10";
    case "qa_failed":
      return "text-destructive border-destructive/40 bg-destructive/10";
    case "submitted":
    case "in_review":
      return "text-warning border-warning/40 bg-warning/10";
    case "in_progress":
      return "text-primary border-primary/40 bg-primary/10";
    default:
      return "text-muted-foreground border-border bg-muted/40";
  }
}

function phaseStatusLabel(status: string | undefined): string {
  if (!status) return "Not started";
  return humanizeStatus(status);
}

function formatPhaseDisplayName(phaseName: string): string {
  const phaseMap: Record<string, string> = {
    prep: "Arrival, Prep & Protection",
    demo: "Demolition & Structural Assessment",
    plumbing: "Framing, Plumbing & Rough-In",
    install: "Pan, Substrate & Wall Installation",
    qa: "Final QA, Water Testing & Closeout",
  };
  return phaseMap[phaseName.toLowerCase()] ?? phaseName;
}

/**
 * On-pace heuristic (read-only, presentation only).
 * Compares observed completion ratio vs. the expected ratio for an
 * 8a–6p install day. Hard signals (blocked, QA fail, stale) short-circuit.
 */
function derivePace(
  row: InstallsLiveRow,
  lifecycle: Lifecycle | undefined,
  now: Date,
): { label: string; tone: string } {
  if (lifecycle === "blocked")
    return { label: "Blocked", tone: "text-destructive border-destructive/40 bg-destructive/10" };
  if (row.qaFailuresTotal > 0)
    return { label: "Behind · QA fail", tone: "text-destructive border-destructive/40 bg-destructive/10" };
  if (row.isStale)
    return { label: "Stalled", tone: "text-destructive border-destructive/40 bg-destructive/10" };

  const allDone =
    row.phases.length > 0 && row.phases.every((p) => p.status === "qa_passed");
  if (allDone || lifecycle === "complete")
    return { label: "Complete", tone: "text-success border-success/40 bg-success/10" };

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const scheduledToday =
    row.scheduledInstallDate &&
    row.scheduledInstallDate.startsWith(today.toISOString().slice(0, 10));
  if (!scheduledToday) {
    if (!row.executionAvailable)
      return { label: "Not started", tone: "text-muted-foreground border-border bg-muted/40" };
    return { label: "In progress", tone: "text-primary border-primary/40 bg-primary/10" };
  }

  const hoursIntoDay = now.getHours() + now.getMinutes() / 60;
  const expected = Math.max(0, Math.min(1, (hoursIntoDay - 8) / 10)); // 8a→6p window
  const totalUnits =
    row.totalChecklistTotal + row.totalPhotosRequired;
  const doneUnits = row.totalChecklistChecked + row.totalPhotosUploaded;
  const actual = totalUnits > 0 ? doneUnits / totalUnits : 0;
  const diff = actual - expected;

  if (diff >= -0.08)
    return { label: "On pace", tone: "text-success border-success/40 bg-success/10" };
  if (diff >= -0.22)
    return { label: "Slightly behind", tone: "text-warning border-warning/40 bg-warning/10" };
  return { label: "Behind", tone: "text-destructive border-destructive/40 bg-destructive/10" };
}

const PROOFS_BUCKET = 'execution-proofs';

/**
 * Parse any Supabase storage URL or path into { bucket, path }.
 * Returns null if the value is not a recognisable storage reference.
 *
 * Supported formats:
 *  1. "bucket::path/to/file"          ← attachment.service.ts format
 *  2. ".../object/public/{bucket}/…"  ← full public URL
 *  3. ".../object/sign/{bucket}/…"    ← already-signed URL (strip token)
 *  4. "path/to/file"                  ← bare execution-proofs path
 *  5. "photo_*" placeholder strings   ← ignored (not real storage paths)
 */
function parseStoragePath(raw: string): { bucket: string; path: string } | null {
  const value = String(raw ?? '').trim();
  if (!value || value.startsWith('photo_')) return null;
  // Format 1 — "bucket::path"
  if (value.includes('::')) {
    const sep = value.indexOf('::');
    const bucket = value.slice(0, sep);
    const path = value.slice(sep + 2);
    if (bucket && path) return { bucket, path };
  }
  // Format 2 — full public URL
  const pubMatch = value.match(/\/storage\/v1\/object\/public\/([^/?]+)\/(.+?)(?:\?.*)?$/);
  if (pubMatch) return { bucket: pubMatch[1], path: pubMatch[2] };
  // Format 3 — signed URL
  const signMatch = value.match(/\/storage\/v1\/object\/sign\/([^/?]+)\/([^?]+)/);
  if (signMatch) return { bucket: signMatch[1], path: signMatch[2] };
  // Format 4 — execution_proofs.storage_path often stores a bare path.
  if (!/^https?:\/\//i.test(value)) {
    return { bucket: PROOFS_BUCKET, path: value };
  }
  return null;
}

export function JobTriageSheet({
  open,
  onOpenChange,
  row,
  job,
  lifecycle,
  blockerReasons = [],
  blockerLabel,
  blockers = 0,
}: JobTriageSheetProps) {
  const { contractors, crews, specExecutions } = useAppData();
  const execution = specExecutions?.find((e) => e.jobId === row?.jobId) ?? null;
  const phasePhotoKey = useMemo(
    () =>
      (execution?.phases ?? [])
        .flatMap((phase) => phase.photos.map((photo) => photo.url))
        .filter(Boolean)
        .join('\n'),
    [execution],
  );

  // All hooks must be declared before any early returns (Rules of Hooks).
  const [signedProofUrls, setSignedProofUrls] = useState<string[]>([]);
  const [phasePhotoSignedMap, setPhasePhotoSignedMap] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!open || !row?.jobId) {
      setSignedProofUrls([]);
      setPhasePhotoSignedMap(new Map());
      return;
    }
    const fetchProofs = async () => {
      // Collect all raw photo URLs from per-phase execution data.
      // rawUrl → { bucket, path } — keyed by the original URL so the render
      // can look up signed URL using ph.url directly.
      const urlToParsed = new Map<string, { bucket: string; path: string }>();
      for (const phase of execution?.phases ?? []) {
        for (const photo of phase.photos) {
          const parsed = parseStoragePath(photo.url);
          if (parsed) urlToParsed.set(photo.url, parsed);
        }
      }

      // Also collect execution_proofs table rows (keyed by storage_path).
      const { data: proofRows } = await supabase
        .from('execution_proofs')
        .select('storage_path')
        .in('execution_id', [row.jobId])
        .is('superseded_by', null);
      for (const r of (proofRows ?? []) as { storage_path: string }[]) {
        const parsed = parseStoragePath(r.storage_path);
        if (parsed) urlToParsed.set(r.storage_path, parsed);
      }

      if (urlToParsed.size === 0) {
        setSignedProofUrls([]);
        setPhasePhotoSignedMap(new Map());
        return;
      }

      const signedMap = new Map<string, string>();
      await Promise.all(
        [...urlToParsed].map(async ([rawUrl, { bucket, path }]) => {
          const signedUrl = await createSignedStorageThumbnailUrl(path, {
            bucket,
            expiresIn: 3600,
          }).catch((err) => {
            console.error(`[JobTriageSheet] thumbnail signing error for bucket "${bucket}":`, err);
            return "";
          });
          if (signedUrl) signedMap.set(rawUrl, signedUrl);
        }),
      );

      setPhasePhotoSignedMap(signedMap);

      // Populate the flat proof URLs section (execution_proofs table rows only).
      const proofSignedUrls = (proofRows ?? [])
        .map((r: { storage_path: string }) => signedMap.get(r.storage_path))
        .filter((u): u is string => !!u);
      setSignedProofUrls(proofSignedUrls);
    };
    void fetchProofs();
  }, [execution, open, phasePhotoKey, row?.jobId]);

  // Null guard AFTER all hooks — React requires hooks to run unconditionally.
  if (!row) return null;

  const crew = crews?.find(
    (c) => c.name === job?.assignedCrew || c.id === job?.assignedCrew,
  ) ?? null;
  const contractor = contractors?.find(
    (c) =>
      c.id === job?.assignedSubcontractorId ||
      c.companyName === job?.assignedSubcontractor ||
      c.contactName === job?.assignedSubcontractor ||
      c.companyName === row.installerName,
  ) ?? null;
  const leadName: string = (crew as { leadInstallerName?: string })?.leadInstallerName ?? row.installerName ?? "Unassigned";
  const helperNames: string[] = ((crew as { members?: string[] })?.members ?? []).filter((m) => m !== leadName);
  const phaseExecMap = new Map<string, PhaseExecution>(
    (execution?.phases ?? []).map((p) => [p.phaseName, p]),
  );

  const recentActivity = (job?.activityHistory ?? []).slice(-5).reverse();
  const pace = derivePace(row, lifecycle, new Date());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1100px] w-[95vw] p-0 gap-0 max-h-[92vh] flex flex-col overflow-hidden sm:rounded-lg">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/60 space-y-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 text-left">
              <DialogTitle className="text-base truncate flex items-center gap-2">
                <Home className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                {job?.customerName ?? row.customerName}
              </DialogTitle>
              <DialogDescription className="text-[11px] mt-0.5">
                <span className="tabular-nums">{row.jobId}</span>
                {row.city && <> · {row.city}</>}
              </DialogDescription>
            </div>
            <Link
              to={`/jobs/${row.jobId}`}
              onClick={() => onOpenChange(false)}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline mr-6"
            >
              Open full job
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Homeowner contact strip — single source of truth for homeowner info */}
          {(job?.phone || job?.email || job?.address) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground">
              {job?.phone && (
                <a
                  href={`tel:${job.phone}`}
                  className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
                >
                  <Phone className="h-3 w-3" />
                  <span className="tabular-nums text-foreground/90">{job.phone}</span>
                </a>
              )}
              {job?.email && (
                <a
                  href={`mailto:${job.email}?subject=${encodeURIComponent(`Install update — ${row.jobId}`)}`}
                  className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors min-w-0"
                >
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate text-foreground/90">{job.email}</span>
                </a>
              )}
              {job?.address && (
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">{formatAddress(job.address)}</span>
                </span>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className={cn("text-[10px] gap-1", pace.tone)}>
              <CircleDot className="h-2.5 w-2.5" />
              {pace.label}
            </Badge>
            {row.awaitingQa && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 text-warning border-warning/40 bg-warning/10"
              >
                Awaiting QA
              </Badge>
            )}
            {row.qaFailuresTotal > 0 && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 text-destructive border-destructive/40 bg-destructive/10"
              >
                <XCircle className="h-3 w-3" /> {row.qaFailuresTotal} QA fail
              </Badge>
            )}
            {row.reconciliationPending && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 text-warning border-warning/40 bg-warning/10"
              >
                Reconciliation pending
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-12 gap-x-5 gap-y-4 [&>section]:min-w-0">
            {/* At a glance */}
            <section className="col-span-12">
              <div className="rounded-lg border border-border/50 bg-muted/15 px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-4 divide-border/50 md:divide-x">
                <GlanceField
                  icon={<Hammer className="h-3.5 w-3.5" />}
                  label="Active phase"
                  value={
                    row.activePhaseName
                      ? `${row.activePhaseName} · ${phaseStatusLabel(row.activePhaseStatus)}`
                      : "—"
                  }
                />
                <GlanceField
                  icon={<Calendar className="h-3.5 w-3.5" />}
                  label="Scheduled"
                  value={fmtDate(row.scheduledInstallDate)}
                  className="md:pl-4"
                />
                <GlanceField
                  icon={<Users className="h-3.5 w-3.5" />}
                  label="Lead installer"
                  value={leadName}
                  className="md:pl-4"
                />
                <GlanceField
                  icon={<MapPin className="h-3.5 w-3.5" />}
                  label="City"
                  value={row.city || "—"}
                  className="md:pl-4"
                />
              </div>
            </section>

            {/* Blockers */}
            {lifecycle === "blocked" && blockerReasons.length > 0 && (
              <section className="col-span-12 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldAlert className="h-3.5 w-3.5 text-destructive" />
                  <h3 className="text-[11px] font-semibold text-destructive">
                    {blockerLabel || "Blocked"}
                  </h3>
                  {blockers > 0 && (
                    <Badge
                      variant="outline"
                      className="ml-auto text-[10px] text-destructive border-destructive/40"
                    >
                      {blockers} open
                    </Badge>
                  )}
                </div>
                <ul className="text-[11.5px] text-foreground/90 list-disc pl-4 space-y-0.5">
                  {blockerReasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Phase progress + per-phase photos */}
            {row.executionAvailable && row.phases.length > 0 && (
              <section className="col-span-12 lg:col-span-7">
                <h3 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">
                  Progress &amp; photos by phase
                </h3>
                <ol className="space-y-2">
                  {row.phases.map((p) => {
                    const Icon =
                      p.status === "qa_passed"
                        ? CheckCircle2
                        : p.status === "qa_failed"
                          ? XCircle
                          : CircleDot;
                    const iconTone =
                      p.status === "qa_passed"
                        ? "text-success"
                        : p.status === "qa_failed"
                          ? "text-destructive"
                          : "text-muted-foreground";
                    const phaseExec = phaseExecMap.get(p.phaseName);
                    const photos = phaseExec?.photos ?? [];
                    return (
                      <li
                        key={p.phaseName}
                        className="rounded-md border border-border/50 bg-card/40 px-3 py-2.5 space-y-2"
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={cn("h-3.5 w-3.5 shrink-0", iconTone)} />
                          <p className="text-[12.5px] font-medium text-foreground truncate w-20">
                            {formatPhaseDisplayName(p.phaseName)} 
                          </p>
                          <div className="flex-1 min-w-0">
                            <Progress value={p.progressPct} className="h-1" />
                          </div>
                          <div className="flex items-center gap-3 text-[10.5px] text-muted-foreground tabular-nums shrink-0">
                            <span title="Photos uploaded / required">
                              <span className="text-foreground/80">{p.photosUploaded}</span>
                              /{p.photosRequired} ph
                            </span>
                            <span title="Checklist checked / total">
                              <span className="text-foreground/80">{p.checklistChecked}</span>
                              /{p.checklistTotal} ck
                            </span>
                          </div>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] capitalize shrink-0",
                              phaseStatusTone(p.status),
                            )}
                          >
                            {phaseStatusLabel(p.status)}
                          </Badge>
                        </div>
                        {photos.length > 0 ? (
                          <div className="grid grid-cols-6 gap-1.5 pl-6">
                            {photos.map((ph, i) => {
                              // Key by raw ph.url — phasePhotoSignedMap is built with
                              // raw URLs as keys after the multi-bucket signing pass.
                              const signed = phasePhotoSignedMap.get(ph.url);
                              // Only render the <img> once we have a signed URL.
                              // Until then show a skeleton box so the onError handler
                              // can never permanently hide a later-arriving signed URL.
                              return (
                              <a
                                key={i}
                                href={signed || '#'}
                                target={signed ? "_blank" : undefined}
                                rel="noreferrer"
                                title={`${ph.photoType} · ${fmtDateTime(ph.uploadedAt)}`}
                                className="aspect-square rounded border border-border/60 bg-muted/40 overflow-hidden block hover:opacity-90 transition-opacity"
                              >
                                {signed ? (
                                  <img
                                    src={signed}
                                    alt={`Phase photo ${i + 1}`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-full h-full animate-pulse bg-muted/60" />
                                )}
                              </a>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="pl-6 text-[10.5px] text-muted-foreground italic">
                            No photos uploaded for this phase yet.
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}

            {/* Execution proofs section */}
            {signedProofUrls.length > 0 && (
              <section className="col-span-12 lg:col-span-7">
                <h3 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">
                  Execution Proofs
                </h3>
                <div className="grid grid-cols-6 gap-1.5 pl-6">
                  {signedProofUrls.map((url, i) => (
                    <a
                      key={`proof-${i}`}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="aspect-square rounded border border-border/60 bg-muted/40 overflow-hidden block hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={url}
                        alt={`Execution proof ${i + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                        }}
                      />
                    </a>
                  ))}
                </div>
              </section>
            )}

            {!row.executionAvailable && (
              <section className="col-span-12 lg:col-span-7 rounded-md border border-border/60 bg-muted/20 px-3 py-2.5 text-[11.5px] text-muted-foreground">
                {row.unavailableReason ??
                  "Execution data not available yet — checklist and photos will appear once the installer starts the job."}
              </section>
            )}

            {/* Right rail: contacts + activity */}
            <aside className="col-span-12 lg:col-span-5 flex flex-col gap-4 min-w-0">
              {/* Crew on site — promoted to top of the rail */}
              <section className="rounded-md border border-border/50 bg-card/40 p-3">
                <h3 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
                  <Users className="h-3 w-3" /> Crew on site
                </h3>
                <div className="space-y-2">
                  <ContactBlock
                    name={leadName}
                    subtitle="Lead installer"
                    phone={
                      crew?.memberPhones?.[leadName] ??
                      crew?.leadInstallerPhone ??
                      contractor?.phone
                    }
                    email={contractor?.email}
                    emailSubject={`Job ${row.jobId}`}
                  />
                  {helperNames.length > 0 ? (
                    helperNames.map((h) => (
                      <ContactBlock
                        key={h}
                        name={h}
                        subtitle="Helper"
                        phone={crew?.memberPhones?.[h] ?? null}
                        email={null}
                      />
                    ))
                  ) : (
                    <p className="text-[10.5px] text-muted-foreground italic pl-0.5">
                      No helper recorded on this crew.
                    </p>
                  )}
                </div>
              </section>

              {recentActivity.length > 0 && (
                <section>
                  <h3 className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium mb-2">
                    Recent activity
                  </h3>
                  <ol className="relative space-y-2.5 pl-3 before:absolute before:left-[3px] before:top-1.5 before:bottom-1.5 before:w-px before:bg-border/60">
                    {recentActivity.map((a) => (
                      <li key={a.id} className="relative text-[11.5px]">
                        <span className="absolute -left-3 top-1 h-1.5 w-1.5 rounded-full bg-muted-foreground/70 ring-2 ring-background" />
                        <p className="text-foreground/90 leading-snug">{a.action}</p>
                        <p className="text-[10.5px] text-muted-foreground mt-0.5">
                          {fmtDateTime(a.date)} · {a.user}
                        </p>
                      </li>
                    ))}
                  </ol>
                </section>
              )}
            </aside>

            <div className="col-span-12">
              <Separator />
              <p className="text-[10px] text-muted-foreground text-center pt-2 pb-1">
                Read-only view · changes happen in the full job
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GlanceField({
  icon,
  label,
  value,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
        {icon}
        {label}
      </div>
      <p className="text-[12.5px] text-foreground mt-1 truncate" title={value}>
        {value}
      </p>
    </div>
  );
}
